// The "⋯" menu at the end of a table row. Keeps the Actions column one
// button wide however many actions a row has; destructive actions go last,
// below a divider, in red.
//
//   <RowActions label={cls.name} actions={[
//       { label: "Students", icon: Users, onSelect: () => … },
//       { label: "Delete", icon: Trash2, onSelect: () => …, destructive: true, hidden: !canManage },
//   ]} />
//
// Clicks on the menu don't reach the row, so a row can have its own onClick.
import type { LucideIcon } from "lucide-react";
import { Loader2, MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type RowAction = {
    label: string;
    icon?: LucideIcon;
    onSelect: () => void;
    destructive?: boolean;
    hidden?: boolean;
    disabled?: boolean;
};

type Props = {
    actions: RowAction[];
    label?: string;     // menu heading, e.g. the row's name
    busy?: boolean;     // a row action is running: spinner instead of the dots
};

export function RowActions({ actions, label, busy }: Props) {
    const shown = actions.filter(a => !a.hidden);
    if (shown.length === 0) return null;
    const normal = shown.filter(a => !a.destructive);
    const danger = shown.filter(a => a.destructive);

    const item = (a: RowAction) => (
        <DropdownMenuItem
            key={a.label}
            disabled={a.disabled}
            variant={a.destructive ? "destructive" : "default"}
            onSelect={a.onSelect}
        >
            {a.icon && <a.icon className="h-4 w-4" />}
            {a.label}
        </DropdownMenuItem>
    );

    return (
        <div onClick={(e) => e.stopPropagation()} className="inline-flex">
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={busy} aria-label={label ? `Actions for ${label}` : "Actions"}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                    {label && <DropdownMenuLabel className="truncate text-xs text-muted-foreground font-normal">{label}</DropdownMenuLabel>}
                    {normal.map(item)}
                    {normal.length > 0 && danger.length > 0 && <DropdownMenuSeparator />}
                    {danger.map(item)}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
